<template>
  <div class="hc-search" @keydown.esc.prevent="clear">
    <div class="hc-search__control" :class="{ 'is-focused': focused, 'is-loading': loading, 'has-value': !!search }">
      <span class="hc-search__icon">
        <LoupeIcon/>
      </span>
      <input
        class="hc-search__input"
        v-model.trim="search"
        placeholder="Search"
        autocomplete="off"
        @focus="focused = true"
        @blur="onBlur"
      />
      <button
        v-if="search && !loading"
        class="hc-search__clear"
        type="button"
        title="Clear"
        @mousedown.prevent
        @click="clear"
      >
        <CloseSearch/>
      </button>
      <span v-if="loading" class="hc-search__spinner"></span>
    </div>
    <div v-if="showDropdown" class="hc-search__dropdown" @mousedown.prevent>
      <div v-if="!results.length && !loading" class="hc-search__empty">Nothing found</div>
      <template v-else>
        <div v-if="results && results.length > 0" class="hc-search__title">
          <ListIcon/>
          <span>Reservation</span>
        </div>
        <ul class="hc-search__list" ref="list" @scroll="onListScroll">
          <li v-for="res in results" :key="res.id" @click="onBookingClick(res.id)" class="hc-search__item">
            <div class="hc-search__item-left">
              <div class="hc-search__name">{{ res.group_name }}</div>
              <div class="hc-search__sub">
                {{ res.property && res.property.name ? res.property.name : '' }}
                <template v-if="res.source && res.source.name"> — {{ res.source.name }}</template>
              </div>
            </div>
            <div>
              <div v-if="res.deadline_text" class="hc-search__deadline">
                <span class="hc-search__deadline-label">Deadline</span>
                <span class="hc-search__deadline-pill">{{ res.deadline_text }}</span>
              </div>
            </div>
            <div class="hc-search__item-right">
              <div class="hc-search__dates" v-if="res.date_text">{{ res.date_text }}</div>
              <div class="hc-search__status" :class="'hc-search__status--' + res.status.code">
                {{ res.status.name }}
              </div>
            </div>
          </li>
          <li v-if="loadingMore" class="hc-search__empty">Loading…</li>
          <li v-if="!loadingMore && !hasMore && results.length" class="hc-search__empty">No more results</li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { parseISO, format } from 'date-fns'
import { getHotelManagementReservationsV2 } from '@/http'
import LoupeIcon from '@/views/hotel-management/calendar/icons/LoupeIcon.vue'
import ListIcon from '@/views/hotel-management/calendar/icons/ListIcon.vue'
import CloseSearch from '@/views/hotel-management/calendar/icons/CloseSearch.vue'

export default {
  name: 'HcSearch',
  components: { CloseSearch, ListIcon, LoupeIcon },
  data() {
    return {
      search: '',
      focused: false,
      loading: false,
      results: [],
      page: 1,
      pageSize: 20,
      hasMore: false,
      loadingMore: false,
      currentQuery: '',
      fetchToken: 0,
      totalCount: 0,
      totalPages: 0,
      nextUrl: null,
      debouncedFetch: null
    }
  },
  computed: {
    showDropdown() {
      return this.focused && (this.loading || !!this.search || this.results.length)
    }
  },
  watch: {
    search() {
      this.loading = !!this.search
      this.debouncedFetch()
    }
  },
  created() {
    this.debouncedFetch = debounce(this.onSearchDebounced, 500)
  },
  methods: {
    onSearchDebounced() {
      this.page = 1
      this.results = []
      this.hasMore = false
      this.currentQuery = this.search || ''
      this.totalCount = 0
      this.totalPages = 0
      this.nextUrl = null

      if (!this.currentQuery) {
        this.loading = false
        return
      }
      this.fetchPage({ reset: true })
    },
    async fetchPage({ reset = false } = {}) {
      const token = ++this.fetchToken
      const isFirstPage = this.page === 1

      try {
        if (!isFirstPage && !this.hasMore) return
        if (!isFirstPage) this.loadingMore = true

        const params = {
          search: this.currentQuery,
          page: this.page,
          page_size: this.pageSize
        }

        const res = await getHotelManagementReservationsV2(params)

        if (token !== this.fetchToken) return

        const payload = res?.data || {}
        const items = Array.isArray(payload.results) ? payload.results : []
        const normalized = items.map(this.normalizeReservation)

        this.totalCount = Number(payload.count || 0)
        this.totalPages = Number(payload.pages || 0)
        this.nextUrl = payload.next || null
        this.hasMore = !!this.nextUrl || (this.page < this.totalPages)

        if (reset || isFirstPage) {
          this.results = normalized
        } else {
          this.results = this.results.concat(normalized)
        }

        if (this.hasMore) this.page += 1
      } catch (e) {
        if (token !== this.fetchToken) return
        if (this.page === 1) this.results = []
        this.hasMore = false
        this.nextUrl = null
      } finally {
        if (token === this.fetchToken) {
          this.loading = false
          this.loadingMore = false
        }
      }
    },
    onListScroll(e) {
      if (this.loadingMore || this.loading || !this.hasMore) return
      const el = e.target
      const threshold = 120
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
      if (nearBottom) this.fetchPage()
    },
    onBookingClick(id) {
      this.$router.push({ name: 'hotel-management-reservations-detail-reservation', params: { id } })
      if (this.$route.name === 'hotel-management-reservations-detail-reservation') location.reload()
    },
    normalizeReservation(r) {
      const inDate = r.start_date ? parseISO(r.start_date) : null
      const outDate = r.end_date ? parseISO(r.end_date) : null
      const date_text = (inDate && outDate) ? `${ format(inDate, 'dd/MM/yyyy') } — ${ format(outDate, 'dd/MM/yyyy') }` : ''
      const deadline_text = (r.deadline && r.deadline.date) ? format(parseISO(r.deadline.date), 'd MMM, yyyy') : ''
      return { ...r, date_text, deadline_text }
    },
    clear() {
      this.search = ''
      this.results = []
      this.loading = false
      this.loadingMore = false
      this.hasMore = false
      this.page = 1
      this.currentQuery = ''
      this.totalCount = 0
      this.totalPages = 0
      this.nextUrl = null
      this.fetchToken++
    },
    onBlur() {
      setTimeout(() => {
        this.focused = false
      }, 0)
    }
  }
}
</script>

<style scoped>
.hc-search {
  --hc-border: #e5e7eb;
  --hc-border-focus: #cbd5e1;
  --hc-text: #111827;
  --hc-muted: #6b7280;
  --hc-bg: #fff;
  --hc-shadow: 0 1px 2px rgba(0, 0, 0, .06);
  --hc-shadow-lg: 0 8px 24px rgba(0, 0, 0, .12);
  position: relative;
  width: 670px;
}

.hc-search__control {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid var(--hc-border);
  border-radius: 20px;
  background: var(--hc-bg);
  padding: 8px 40px 8px 46px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  transition: border-color .15s ease;
}

.hc-search__control.is-focused {
  border-color: var(--hc-border-focus)
}

.hc-search__icon {
  position: absolute;
  left: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hc-search__input {
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  font-family: 'CoreSansCR', sans-serif;
  color: #000;
  outline: none;
  border: 0;
  background: transparent;
}

.hc-search__input::placeholder {
  color: #707070
}

.hc-search__clear {
  position: absolute;
  right: 12px;
  width: 26px;
  height: 26px;
  border: 0;
  background: transparent;
  color: #9ca3af;
  border-radius: 50%;
  cursor: pointer;
}

.hc-search__clear:hover {
  color: #6b7280
}

.hc-search__spinner {
  position: absolute;
  right: 14px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  border-top-color: #94a3b8;
  animation: hc-spin .8s linear infinite;
}

@keyframes hc-spin {
  to {
    transform: rotate(360deg)
  }
}

.hc-search__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid var(--hc-border);
  border-radius: 16px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 20;
}

.hc-search__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #5a8e9a;
  padding: 11px 20px;
  background-color: #f6f6f6;
}

.hc-search__empty {
  padding: 16px 20px;
  font-size: 14px;
  color: var(--hc-muted);
}

.hc-search__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 420px;
  overflow: auto;
}

.hc-search__item {
  display: grid;
  grid-template-columns: 1fr 110px 180px;
  gap: 12px 16px;
  padding: 13px 22px 13px 20px;
  border-top: 1px solid #f1f5f9;
  transition: background-color 0.3s;
}

.hc-search__item:hover {
  cursor: pointer;
  background-color: #fafafa
}

.hc-search__item:first-child {
  border-top: 0
}

.hc-search__item-left {
  min-width: 0
}

.hc-search__name {
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #091411;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hc-search__sub {
  margin-top: 4px;
  font-family: 'CoreSansCR', sans-serif;
  color: #666;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hc-search__item-right {
  display: grid;
  align-content: center;
  justify-items: end;
  gap: 6px;
  text-align: right;
}

.hc-search__dates {
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  color: #000;
}

.hc-search__status {
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  font-weight: 500;
}

.hc-search__status--booked {
  color: #89bd7b
}

.hc-search__status--cancelled {
  color: #da8c7b
}

.hc-search__status--booked_provisionally {
  color: #76a9b4
}

.hc-search__deadline {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 6px
}

.hc-search__deadline-label {
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  color: #000;
}

.hc-search__deadline-pill {
  padding: 4px 8px;
  border-radius: 6px;
  background-color: #f6f6f6;
  font-family: 'CoreSansCR', sans-serif;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}
</style>
